import type { PoolDef } from '../../shared/types';
import { fmtCompact, fmtPct } from '../../shared/format';
import { GAS_RESERVE_ETH, blendedYield, gasLevel, type GasRow, type PoolPosition } from '../lib/positions';

type Props = { summary: PoolPosition[]; gas: GasRow[]; pools: PoolDef[] };

const short = (a: string) => `${a.slice(0, 6)}…${a.slice(-4)}`;

/** Per-pool position, its APY, share of the pool, and how much of the exit door you would need; blended yield in the footer. */
export function PositionsTable({ summary, gas, pools }: Props) {
  const name = (id: string) => pools.find((p) => p.id === id);
  const usdc = summary.filter((s) => name(s.poolId)?.asset === 'USDC').reduce((n, s) => n + s.total, 0);
  const bold = summary.filter((s) => name(s.poolId)?.asset === 'BOLD').reduce((n, s) => n + s.total, 0);
  const lowGas = gas.filter((g) => gasLevel(g.eth) !== 'ok');
  const yieldNow = blendedYield(summary);
  const totalUsd = summary.reduce((n, s) => n + s.usd, 0);
  return (
    <section class="panel">
      <div class="panel-title-row">
        <h2 class="panel-title panel-title--flush">Positions</h2>
        <div class="chips">
          <span class="chip">{fmtCompact(usdc)} USDC</span>
          {bold > 0 && <span class="chip">{fmtCompact(bold)} BOLD</span>}
        </div>
      </div>
      {lowGas.length > 0 && (
        <div class="banner" role="status">
          Gas reserve below {GAS_RESERVE_ETH} ETH on {lowGas.map((g) => g.label).join(', ')}. Exits cost gas; top up before you need to move.
        </div>
      )}
      {summary.length === 0 ? (
        <p class="panel-note">No positions found at these addresses.</p>
      ) : (
        <div class="table-wrap">
          <table class="positions-table mono">
            <thead>
              <tr><th>Venue</th><th>Position</th><th>APY 7d</th><th>Of pool</th><th>Of exit liquidity</th></tr>
            </thead>
            <tbody>
              {summary.map((s) => {
                const p = name(s.poolId);
                return (
                  <>
                    <tr key={s.poolId} class={`pos pos--${s.level}`}>
                      <td><span class="pos-name"><i class="pos-dot" aria-hidden="true" />{p?.name ?? s.poolId}</span></td>
                      <td>{fmtCompact(s.total)} {p?.asset}</td>
                      <td>{fmtPct(s.apy, 2)}</td>
                      <td>{fmtPct(s.exposure, 2)}</td>
                      <td>
                        <span class={`pill ${s.level === 'ok' ? '' : `pill--${s.level}`}`}>{s.exitCoverage === Infinity ? 'pool drained' : fmtPct(s.exitCoverage)}</span>
                      </td>
                    </tr>
                    {s.rows.length > 1 &&
                      s.rows.map((r) => (
                        <tr key={`${s.poolId}|${r.address}`} class="pos-row">
                          <td>↳ {r.label} <span class="dim">{short(r.address)}</span></td>
                          <td>{fmtCompact(r.assets)}{r.collGain ? ` · +${r.collGain.toFixed(4)} coll` : ''}</td>
                          <td colSpan={3} />
                        </tr>
                      ))}
                  </>
                );
              })}
            </tbody>
            <tfoot>
              <tr class="pos-total">
                <td>Blended</td>
                <td>${fmtCompact(totalUsd)}</td>
                <td>{fmtPct(yieldNow.apy, 2)}</td>
                <td colSpan={2}>${fmtCompact(yieldNow.monthly)}/mo · ${fmtCompact(yieldNow.annual)}/yr</td>
              </tr>
              {yieldNow.uncovered > 0.5 && (
                <tr class="pos-row">
                  <td colSpan={5}>Excludes ${fmtCompact(yieldNow.uncovered)} in venues without an APY reading.</td>
                </tr>
              )}
            </tfoot>
          </table>
        </div>
      )}
      <ul class="gas-list mono">
        {gas.map((g) => (
          <li key={g.address} class={gasLevel(g.eth) === 'ok' ? '' : 'loss'}>
            {g.label} <span class="dim">{short(g.address)}</span> · {g.eth.toFixed(4)} ETH
          </li>
        ))}
      </ul>
    </section>
  );
}
